import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { API_URL } from '../config';
import { 
  Activity,
  AlertTriangle,
  CheckCircle2,
  Shield,
  Eye,
  Brain,
  Sparkles,
  MapPin,
  Scissors,
  Syringe,
  Wind,
  Heart,
  Droplets
} from 'lucide-react';

export default function DrainageThoraciquePage() {
  const [checkedItems, setCheckedItems] = useState({});
  const [patientData, setPatientData] = useState({
    traumaType: '',
    memberTrauma: false,
    isFemale: false,
    punctionSite: ''
  });
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiResult, setAiResult] = useState(null);

  const handleCheck = (section, item) => {
    setCheckedItems(prev => ({
      ...prev,
      [`${section}-${item}`]: !prev[`${section}-${item}`]
    }));
  };

  const handlePatientDataChange = (field, value) => {
    setPatientData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Auto-select puncture site based on criteria
      if (field === 'memberTrauma' || field === 'isFemale') {
        if (newData.memberTrauma) {
          newData.punctionSite = 'anterior';
        } else if (newData.isFemale) {
          newData.punctionSite = 'lateral';
        } else {
          newData.punctionSite = 'lateral';
        }
      }
      
      return newData;
    });
  };

  const handleAIAnalysis = async () => {
    setAiAnalyzing(true);
    try {
      const backendUrl = API_URL;
      const response = await fetch(`${backendUrl}/api/ai/analyze-drainage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patientData,
          checkedItems
        })
      });

      if (!response.ok) throw new Error('Erreur lors de l\'analyse IA');

      const data = await response.json();
      setAiResult(data.analysis);
      toast.success('Analyse IA terminée');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors de l\'analyse IA');
    } finally {
      setAiAnalyzing(false);
    }
  };

  const checklistData = {
    indications: {
      title: "01 - INDICATIONS",
      icon: AlertTriangle,
      items: [
        { 
          id: "epanchement_gazeux",
          label: "Épanchement gazeux (pneumothorax)",
          detail: "Présence d'air dans la cavité pleurale"
        },
        { 
          id: "epanchement_sanguin",
          label: "Épanchement sanguin (hémothorax)",
          detail: "Présence de sang dans la cavité pleurale"
        },
        { 
          id: "epanchement_mixte",
          label: "Épanchement mixte (hémopneumothorax)",
          detail: "Présence d'air et de sang dans la cavité pleurale"
        },
        { 
          id: "trauma_thoracique",
          label: "Traumatisme thoracique avec pronostic vital engagé",
          detail: "Indication urgente en préhospitalier",
          important: true
        }
      ]
    },
    preparation: {
      title: "02 - PRÉPARATION & ASEPSIE",
      icon: Shield,
      items: [
        { 
          id: "desinfection",
          label: "Désinfection cutanée à la Bétadine®",
          detail: "Désinfection large de la zone",
          important: true
        },
        { 
          id: "champs_steriles",
          label: "Champs stériles",
          detail: "Mise en place de champs stériles"
        },
        { 
          id: "gants_steriles",
          label: "Gants stériles",
          detail: "Port de gants stériles obligatoire"
        },
        { 
          id: "materiel_pret",
          label: "Matériel prêt",
          detail: "Drain, valve Heimlich, tubulure, clamp, Red-o-pack"
        }
      ]
    },
    technique: {
      title: "03 - TECHNIQUE DE POSE",
      icon: Scissors,
      items: [
        { 
          id: "anesthesie",
          label: "Anesthésie locale à la Xylocaïne®",
          detail: "Peau, muscle, plèvre pariétale",
          important: true
        },
        { 
          id: "incision_axe",
          label: "Incision dans l'axe des fibres du pectoral",
          detail: "Incision au bord supérieur de la côte"
        },
        { 
          id: "incision_profonde",
          label: "Incision de 1 cm de long et de profondeur",
          detail: "Dissection à la pince ou au doigt"
        },
        { 
          id: "insertion",
          label: "Insertion du mandrin + trocart en douceur",
          detail: "Trajet à 90° du plan cutané, vers le haut",
          important: true
        },
        { 
          id: "retrait_trocart",
          label: "Retrait du trocart",
          detail: "Conserver le mandrin qui devient le drain"
        },
        { 
          id: "fixation",
          label: "Fixation solide du drain à la peau",
          detail: "Suture ou système de fixation"
        },
        { 
          id: "valve_heimlich",
          label: "Connection de la valve anti-retour d'Heimlich",
          detail: "Essentielle pour empêcher le retour"
        },
        { 
          id: "pansement",
          label: "Pansement cutané sans coudure du drain",
          detail: "Éviter toute coudure du drain"
        }
      ]
    },
    surveillance: {
      title: "04 - SURVEILLANCE",
      icon: Eye,
      items: [
        { 
          id: "monitoring",
          label: "Monitorage : Scope, PNI, FC, SpO2",
          detail: "Surveillance continue des paramètres vitaux",
          important: true
        },
        { 
          id: "auscultation",
          label: "Auscultation pulmonaire",
          detail: "Vérification de la réexpansion pulmonaire"
        },
        { 
          id: "hemodynamique",
          label: "Amélioration de l'état hémodynamique",
          detail: "Surveillance de la tension artérielle"
        },
        { 
          id: "hematose",
          label: "Amélioration de l'hématose",
          detail: "Surveillance de la SpO2 et de la FR"
        },
        { 
          id: "debit_drainage",
          label: "Contrôle du débit de drainage",
          detail: "Noter le volume et l'aspect du liquide drainé"
        }
      ]
    }
  };

  const punctionSites = [
    {
      id: 'lateral',
      title: 'Site Latéral (Prioritaire)',
      location: 'Ligne axillaire moyenne, 4ème espace intercostal',
      indications: [
        'Priorité chez la jeune femme (esthétique)',
        'Préconisé en l\'absence de trauma du membre supérieur',
        'Moins visible sous les vêtements'
      ],
      color: 'bg-cyan-500/10 border-cyan-500/30',
      icon: MapPin,
      iconColor: 'text-cyan-400'
    },
    {
      id: 'anterior',
      title: 'Site Antérieur',
      location: 'Ligne médioclaviculaire, 2ème espace intercostal',
      indications: [
        'À privilégier en cas de trauma du membre supérieur homolatéral',
        'Accès plus rapide en urgence',
        'Utilisé pour exsufflation à l\'aiguille'
      ],
      color: 'bg-amber-500/10 border-amber-500/30',
      icon: MapPin,
      iconColor: 'text-amber-400'
    }
  ];

  const complications = [
    {
      type: 'Risque Viscéral',
      description: 'Rupture diaphragmatique avec issue des organes abdominaux en intrathoracique',
      detection: 'Auscultation à la recherche de bruits hydroaériques',
      severity: 'high'
    },
    {
      type: 'Risque Vasculaire',
      description: 'Plaie vasculaire (ex: artère mammaire interne)',
      detection: 'Surveillance hémodynamique, saignement abondant',
      severity: 'high'
    },
    {
      type: 'Infection',
      description: 'Infection du site d\'insertion ou pleurésie',
      detection: 'Fièvre, douleur, écoulement purulent',
      severity: 'medium'
    },
    {
      type: 'Dysfonctionnement',
      description: 'Obstruction, coudure, déconnexion du drain',
      detection: 'Absence d\'oscillations, accumulation de liquide',
      severity: 'medium'
    }
  ];

  const pointsCles = [
    {
      title: 'Trajet du drain',
      content: 'Vers le haut pour pneumothorax, en déclive pour épanchement liquide',
      icon: Wind
    },
    {
      title: 'Lieu de ponction',
      content: 'Au-dessus du bord supérieur de la côte inférieure (éviter paquet vasculo-nerveux)',
      icon: MapPin
    },
    {
      title: 'Asepsie rigoureuse',
      content: 'Désinfection large, champs et gants stériles obligatoires',
      icon: Shield
    },
    {
      title: 'Valve anti-retour',
      content: 'Essentielle pour permettre évacuation et empêcher le retour',
      icon: CheckCircle2
    },
    {
      title: 'Hémothorax',
      content: 'Mettre en place le système d\'autotransfusion si disponible',
      icon: Droplets
    },
    {
      title: 'Plaie thoracique',
      content: 'Éviter l\'introduction du drain par une plaie existante',
      icon: AlertTriangle
    }
  ];

  return (
    <div className="min-h-screen bg-[hsl(var(--bg))] text-[hsl(var(--text))] p-4 md:p-6" data-testid="drainage-thoracique-page">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-3">
          <div className="flex items-center justify-center gap-3">
            <Activity className="w-10 h-10 text-cyan-400" />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-cyan-300 bg-clip-text text-transparent">
              Drainage Thoracique
            </h1>
          </div>
          <p className="text-[hsl(var(--text-dim))] text-sm md:text-base max-w-3xl mx-auto">
            Protocole complet pour la pose d'un drain thoracique en urgence (traumatisme thoracique)
          </p>
        </div>

        {/* Configuration Patient */}
        <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]" data-testid="patient-config-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-cyan-300">
              <MapPin className="w-5 h-5" />
              Configuration Patient & Site de Ponction
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="traumaType" className="text-sm text-[hsl(var(--text))]">Type d'épanchement</Label>
              <select
                id="traumaType"
                value={patientData.traumaType}
                onChange={(e) => handlePatientDataChange('traumaType', e.target.value)}
                className="w-full mt-1 px-3 py-2 bg-transparent border border-[hsl(var(--border))] rounded-md text-[hsl(var(--text))]"
                data-testid="trauma-type-select"
              >
                <option value="">Sélectionner...</option>
                <option value="pneumothorax">Pneumothorax (gazeux)</option>
                <option value="hemothorax">Hémothorax (sanguin)</option>
                <option value="hemopneumothorax">Hémopneumothorax (mixte)</option>
              </select>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="memberTrauma"
                  checked={patientData.memberTrauma}
                  onCheckedChange={(checked) => handlePatientDataChange('memberTrauma', checked)}
                  data-testid="member-trauma-checkbox"
                />
                <Label htmlFor="memberTrauma" className="text-sm cursor-pointer">
                  Trauma du membre supérieur homolatéral
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="isFemale"
                  checked={patientData.isFemale}
                  onCheckedChange={(checked) => handlePatientDataChange('isFemale', checked)}
                  data-testid="is-female-checkbox"
                />
                <Label htmlFor="isFemale" className="text-sm cursor-pointer">
                  Patiente (jeune femme)
                </Label>
              </div>
            </div>

            {/* Recommandation du site */}
            {patientData.punctionSite && (
              <Alert className={`${patientData.punctionSite === 'lateral' ? 'border-cyan-500/30 bg-cyan-500/10' : 'border-amber-500/30 bg-amber-500/10'}`}>
                <MapPin className={`w-4 h-4 ${patientData.punctionSite === 'lateral' ? 'text-cyan-400' : 'text-amber-400'}`} />
                <AlertDescription>
                  <p className="font-semibold mb-1">
                    Site recommandé : {patientData.punctionSite === 'lateral' ? 'Latéral' : 'Antérieur'}
                  </p>
                  <p className="text-sm text-[hsl(var(--text-dim))]">
                    {patientData.punctionSite === 'lateral' 
                      ? 'Ligne axillaire moyenne, 4ème espace intercostal'
                      : 'Ligne médioclaviculaire, 2ème espace intercostal'
                    }
                  </p>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Sites de Ponction */}
        <div className="grid md:grid-cols-2 gap-6">
          {punctionSites.map((site) => {
            const SiteIcon = site.icon;
            return (
              <Card 
                key={site.id}
                className={`${site.color} border-2`}
                data-testid={`site-${site.id}`}
              >
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-[hsl(var(--text))]">
                    <SiteIcon className={`w-5 h-5 ${site.iconColor}`} />
                    {site.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="p-3 rounded-lg bg-[hsl(var(--bg))] border border-[hsl(var(--border))]">
                    <p className="text-sm font-semibold text-[hsl(var(--text))] mb-1">Localisation :</p>
                    <p className="text-sm text-[hsl(var(--text-dim))]">{site.location}</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[hsl(var(--text))] mb-2">Indications :</p>
                    <ul className="space-y-1">
                      {site.indications.map((indication, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-[hsl(var(--text-dim))]">
                          <span className="mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-cyan-500" />
                          <span>{indication}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Checklist */}
        <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]" data-testid="drainage-checklist">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-cyan-300">
              <CheckCircle2 className="w-5 h-5" />
              Checklist de Procédure
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="multiple" className="space-y-4">
              {Object.entries(checklistData).map(([key, section]) => {
                const SectionIcon = section.icon;
                return (
                  <AccordionItem 
                    key={key} 
                    value={key}
                    className="border border-[hsl(var(--border))] rounded-lg overflow-hidden bg-[hsl(var(--bg))]"
                    data-testid={`checklist-section-${key}`}
                  >
                    <AccordionTrigger className="px-4 py-3 hover:bg-[hsl(var(--bg-elev))] transition-colors">
                      <div className="flex items-center gap-3">
                        <SectionIcon className="w-5 h-5 text-cyan-400" />
                        <span className="font-semibold text-[hsl(var(--text))]">{section.title}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4">
                      <div className="space-y-3 mt-3">
                        {section.items.map(item => (
                          <div 
                            key={item.id}
                            className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                              checkedItems[`${key}-${item.id}`]
                                ? 'bg-cyan-500/10 border-cyan-500/30'
                                : item.important
                                  ? 'bg-amber-500/5 border-amber-500/20'
                                  : 'bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]'
                            }`}
                            data-testid={`checklist-item-${key}-${item.id}`}
                          >
                            <Checkbox
                              id={`${key}-${item.id}`}
                              checked={checkedItems[`${key}-${item.id}`] || false}
                              onCheckedChange={() => handleCheck(key, item.id)}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <label 
                                htmlFor={`${key}-${item.id}`}
                                className="font-medium text-[hsl(var(--text))] cursor-pointer block"
                              >
                                {item.label}
                              </label>
                              <p className="text-sm text-[hsl(var(--text-dim))] mt-1">{item.detail}</p>
                              {item.important && (
                                <Badge variant="outline" className="mt-2 border-amber-500/30 text-amber-300 text-xs">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  Critique
                                </Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </CardContent>
        </Card>

        {/* Complications */}
        <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-300">
              <AlertTriangle className="w-5 h-5" />
              05 - Complications Potentielles
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              {complications.map((comp, idx) => (
                <div 
                  key={idx}
                  className={`p-4 rounded-lg border ${
                    comp.severity === 'high' 
                      ? 'bg-red-500/10 border-red-500/30' 
                      : 'bg-amber-500/10 border-amber-500/30'
                  }`}
                >
                  <h4 className={`font-semibold mb-2 ${
                    comp.severity === 'high' ? 'text-red-300' : 'text-amber-300'
                  }`}>
                    {comp.type}
                  </h4>
                  <p className="text-sm text-[hsl(var(--text-dim))] mb-2">{comp.description}</p>
                  <p className="text-xs text-[hsl(var(--text-dim))]">
                    <strong className="text-[hsl(var(--text))]">Détection :</strong> {comp.detection}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Points Clés */}
        <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-cyan-300">
              <Sparkles className="w-5 h-5" />
              06 - Points Clés À Retenir
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              {pointsCles.map((point, idx) => {
                const PointIcon = point.icon;
                return (
                  <div 
                    key={idx}
                    className="flex items-start gap-3 p-3 rounded-lg bg-cyan-500/5 border border-cyan-500/20"
                  >
                    <PointIcon className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-[hsl(var(--text))] mb-1">{point.title}</h4>
                      <p className="text-sm text-[hsl(var(--text-dim))]">{point.content}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Analyse IA */}
        <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]" data-testid="ai-analysis-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-cyan-300">
              <Brain className="w-5 h-5" />
              Analyse IA
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              onClick={handleAIAnalysis}
              disabled={aiAnalyzing}
              className="w-full bg-cyan-500 hover:bg-cyan-600 text-black font-medium"
              data-testid="ai-analysis-button"
            >
              {aiAnalyzing ? (
                <>
                  <Activity className="w-4 h-4 mr-2 animate-spin" />
                  Analyse en cours...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Analyser avec l'IA
                </>
              )}
            </Button>

            {aiResult && (
              <Alert className="border-cyan-500/30 bg-cyan-500/10" data-testid="ai-result">
                <Brain className="w-4 h-4 text-cyan-400" />
                <AlertDescription className="text-[hsl(var(--text))] whitespace-pre-wrap mt-2">
                  {aiResult}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
